import React, { createRef, useEffect } from 'react'
import { CirclePieSegmentProps } from './CirclePieSegment.types'
import styles from './CirclePieSegment.module.scss'
import cl from 'classnames'
import { AnimatedCounter, Numbers, Text1, Text2 } from 'components/shared'
import { IconArrowDecrease, IconArrowIncrease } from 'assets'
import { BestItem, BestItemRef } from './BestItem'
import { getLabelStyle } from './CirclePieSegment.utils'

const CirclePieSegmentComponent = (props: CirclePieSegmentProps) => {
  const {
    className,
    style,
    percentage,
    circleSize = 150,
    delay = 0,
    percentageSkip = 0,
    speed = 1.5,
    strokeWidth = 12,
    circleBackgroundColor = styles.backgroundHighlight,
    circleFillColor = styles.primary,
    label,
    deltaPercentage,
    num,
    customFormattedTime,
    afterNumLabel,
    isPrice,
    bestItem
  } = props

  const isDeltaPercentagePositive = deltaPercentage >= 0
  const isDeltaPercentageZero = deltaPercentage === 0
  const outerRadius = circleSize / 2
  const innerRadius = circleSize / 2 - strokeWidth / 2
  const scale = innerRadius / outerRadius
  const toDasharray = circleSize * Math.PI
  const circlePercentage = Math.floor(Math.PI * percentage * scale * (circleSize / 100))
  const circlePercentageSkip = Math.floor(
    Math.PI * percentageSkip * scale * (circleSize / 100)
  )
  const uniqueModifier = Math.floor(Math.random() * 100000)

  const containerRef = createRef<HTMLDivElement>()
  const bestItemRef: BestItemRef = createRef()
  useEffect(() => {
    if (
      containerRef.current.offsetLeft >
      containerRef.current.parentElement.clientWidth / 2
    ) bestItemRef.current.setPositionLeft(true)
  }, [containerRef, bestItemRef])

  return (
    <div
      ref={containerRef}
      className={cl(styles.container, className)}
      style={style}
      onMouseEnter={() => bestItemRef.current.showComponent(true)}
      onMouseLeave={() => bestItemRef.current.showComponent(false)}
    >
      <style>
        {`
           @-webkit-keyframes animate-progress-bar--${uniqueModifier} {
               0% {
                 stroke-dasharray: 0 ${toDasharray}; 
                 visibility: visible;
                 }
             100% {
               stroke-dasharray: ${circlePercentage} ${toDasharray}; 
               visibility: visible;
               }
           }

           @keyframes animate-progress-bar {
                0% { stroke-dasharray: 0 ${toDasharray}; }
              100% { stroke-dasharray: ${circlePercentage} ${toDasharray}; }
            }
         `}
      </style>

      <div
        className={styles.percentageCircle}
        style={{
          width: circleSize,
          height: circleSize
        }}
      >

        <div className={styles.innerContainer}>
          <Text1
            className={styles.label}
            style={getLabelStyle(label)}
          >
            {label}
          </Text1>
          <Numbers
            style={{
              fontSize: 16,
              margin: '10px 0'
            }}
          >
            <AnimatedCounter
              num={num}
              customFormattedTime={customFormattedTime}
              isPrice={isPrice}
              delay={delay}
            />
          </Numbers>
          {afterNumLabel && (
            <Text2 className={styles.afterNumLabel}>
              {afterNumLabel}
            </Text2>
          )}
          <Text1
            style={{
              color: isDeltaPercentagePositive
                ? styles.primary
                : styles.error
            }}
          >
            {(isDeltaPercentagePositive && !isDeltaPercentageZero) && '+'}{deltaPercentage}%
            {isDeltaPercentagePositive
              ? isDeltaPercentageZero
                ? null
                : (
                  <IconArrowIncrease
                    style={{
                      marginLeft: 5,
                      fill: styles.primary
                    }}
                  />
                )
              : (
                <IconArrowDecrease
                  style={{
                    marginLeft: 5,
                    fill: styles.error
                  }}
                />
              )}
          </Text1>
        </div>

        <svg
          width={circleSize}
          height={circleSize}
        >
          <circle
            cx={outerRadius}
            cy={outerRadius}
            r={innerRadius}
            stroke={circleBackgroundColor}
            strokeWidth={strokeWidth}
            fill='none'
          />
          <circle
            cx={outerRadius}
            cy={outerRadius}
            r={innerRadius}
            stroke={circleFillColor}
            strokeWidth={strokeWidth}
            fill='none'
            className={cl(styles.circle)}
            style={{
              strokeDashoffset: -(circlePercentageSkip || 0),
              animationName: `animate-progress-bar--${uniqueModifier}`,
              animationDuration: `${speed}s`,
              animationDelay: `${delay}s`,
              animationTimingFunction: 'cubic-bezier(.87, -.41, .19, 1.44)',
              animationFillMode: 'forwards'
            }}
          />
        </svg>

      </div>

      <BestItem
        ref={bestItemRef}
        item={bestItem}
      />

    </div>
  )
}

const CirclePieSegment = CirclePieSegmentComponent
export { CirclePieSegment }
